import LooksOneIcon from '@mui/icons-material/LooksOne';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  CircularProgress,
  Modal,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../../utils/axiosInstance';

// 다크모드 스타일 정의
const darkModeStyles = {
  container: {
    p: 3,
    background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
    width: '100%',
    minHeight: '100vh',
  },
  paper: {
    p: 4,
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
  },
  title: {
    color: '#fff',
    fontSize: '28px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  tableContainer: {
    maxHeight: 600,
    overflowX: 'auto',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  tableHead: {
    backgroundColor: 'rgba(45, 45, 65, 0.9)',
    '& .MuiTableCell-head': {
      color: '#fff',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transition: 'background-color 0.3s ease',
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
    },
    '& .MuiTableCell-body': {
      color: '#fff',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
  },
  tableCell: {
    color: '#fff',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  payButton: {
    minWidth: '60px',
    height: '30px',
    bgcolor: '#DBC000',
    '&:hover': {
      bgcolor: '#ae9700',
    },
    fontSize: '12px',
    fontWeight: 'bold',
  },
  collectButton: {
    minWidth: '60px',
    height: '30px',
    bgcolor: '#2196f3',
    '&:hover': {
      bgcolor: '#0c7cd5',
    },
    fontSize: '12px',
    fontWeight: 'bold',
  },
  backButton: {
    bgcolor: '#9c27b0',
    '&:hover': {
      bgcolor: '#7b1fa2'
    },
    color: '#fff',
    fontWeight: 'bold',
  },
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'rgba(35, 35, 50, 0.95)',
    color: '#fff',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    p: 4,
    borderRadius: 2,
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  modalTextField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#60a5fa',
    },
  },
};

const TreeViewTable = () => {
  const params = useParams();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery('(min-width:600px)');
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);

  // 지급/회수 모달 state들
  const [openModal, setOpenModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [selectedAction, setSelectedAction] = useState(''); // '지급' or '회수'
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const fetchTreeData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/agent/tree/${params.id || ''}`);
        if (response.data.success) {
          setTreeData(response.data.tree);
        } else {
          console.error('트리 데이터 불러오기 실패:', response.data.message);
        }
      } catch (error) {
        console.error('트리 데이터 불러오기 중 오류 발생:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTreeData();
  }, [params.id]);

  // 지급/회수 버튼 클릭 핸들러
  const handlePaymentClick = (agentId, action) => {
    setSelectedAction(action); // '지급' 또는 '회수'
    setSelectedAgentId(agentId); // 어떤 에이전트인지 기억
    setPaymentAmount(''); // 초기화
    setOpenModal(true); // 모달 오픈
  };

  const handleCancel = () => {
    setOpenModal(false);
  };

  // 지급/회수 처리 함수
  const handlePaymentSubmit = async () => {
    setPaymentLoading(true); // 버튼 비활성화 시작
    try {
      const endpoint = selectedAction === '지급' ? '/agent/point/add' : '/agent/point/set';
      const description = selectedAction === '지급' ? '보너스 지급' : '오류 정정';

      const payload = {
        agent_id: selectedAgentId,
        amount: paymentAmount,
        description,
      };

      const response = await axios.post(endpoint, payload);

      if (response.data.success) {
        alert(`${response.data.message}`);
        setPaymentAmount('');
        setOpenModal(false); // 팝업 닫기
        window.location.reload();
      } else {
        alert(`${selectedAction} 실패: ${response.data.message || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error(`${selectedAction} 중 오류 발생:`, error);

      // 서버에서 반환한 오류 메시지 표시
      if (error.response && error.response.data) {
        if (error.response.data.message) {
          alert(`${selectedAction} 실패: ${error.response.data.message}`);
        } else if (error.response.data.error) {
          alert(`${selectedAction} 실패: ${error.response.data.error}`);
        } else {
          alert(`${selectedAction} 처리 중 오류가 발생했습니다.`);
        }
      } else {
        alert(`${selectedAction} 처리 중 서버 연결 오류가 발생했습니다.`);
      }
    } finally {
      setPaymentLoading(false); // 버튼 다시 활성화
    }
  };

  // 에이전트 데이터를 테이블 형식으로 변환
  const convertTreeToRows = (node, level = 0, parentPath = []) => {
    const levelName = level === 0 ? '총본사' : level === 1 ? '대본사' : level === 2 ? '본사' : `${level + 1}차하부`;
    const currentPath = [...parentPath, { level, label: node.id }];

    const currentRow = {
      id: node.agent_srl || node.id,
      agent_id: node.id, // 실제 에이전트 아이디
      path: currentPath,
      members: '0명', // 실제 데이터에 맞게 수정 필요
      balance: node.cur_point,
      items: '0', // 실제 데이터에 맞게 수정 필요
      fee: node.fee,
      status: '승인',
    };

    let rows = [currentRow];

    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        rows = [...rows, ...convertTreeToRows(child, level + 1, currentPath)];
      });
    }

    return rows;
  };

  const renderCell = (row, level) => {
    const pathItem = row.path.find(item => item.level === level);
    if (!pathItem) return '';
    return level > 0 ? (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <SubdirectoryArrowRightIcon sx={{ mr: 1, opacity: 0.6, color: '#60a5fa' }} />
        {pathItem.label}
      </Box>
    ) : (
      pathItem.label
    );
  };

  const renderTableRows = rows => {
    return rows.map((row, index) => (
      <TableRow
        key={row.id}
        sx={darkModeStyles.tableRow}
      >
        <TableCell align="center" sx={darkModeStyles.tableCell}>{index + 1}</TableCell>
        <TableCell sx={{ ...darkModeStyles.tableCell, fontWeight: 'bold' }}>{renderCell(row, 0)}</TableCell>
        <TableCell sx={{ ...darkModeStyles.tableCell, fontWeight: 'bold' }}>{renderCell(row, 1)}</TableCell>
        <TableCell sx={{ ...darkModeStyles.tableCell, fontWeight: 'bold' }}>{renderCell(row, 2)}</TableCell>
        <TableCell align="center" sx={darkModeStyles.tableCell}>{row.members}</TableCell>
        <TableCell align="center" sx={darkModeStyles.tableCell}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#4CAF50', fontWeight: 'bold', mb: 1 }}>{String(row.balance).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => handlePaymentClick(row.agent_id, '지급')}
                sx={darkModeStyles.payButton}
              >
                지급
              </Button>
              <Button
                variant="contained"
                onClick={() => handlePaymentClick(row.agent_id, '회수')}
                sx={darkModeStyles.collectButton}
              >
                회수
              </Button>
            </Box>
          </Box>
        </TableCell>
        <TableCell align="center" sx={darkModeStyles.tableCell}>{row.items}</TableCell>
        <TableCell align="center" sx={darkModeStyles.tableCell}>{row.fee}%</TableCell>
        <TableCell align="center" sx={{ ...darkModeStyles.tableCell, color: '#4CAF50', fontWeight: 'bold' }}>
          {row.status}
        </TableCell>
      </TableRow>
    ));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh" sx={{ bgcolor: 'rgba(25,25,38,0.95)' }}>
        <CircularProgress sx={{ color: '#60a5fa' }} />
      </Box>
    );
  }

  if (!treeData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh" sx={{ bgcolor: 'rgba(25,25,38,0.95)' }}>
        <Typography sx={{ color: '#fff' }}>데이터를 불러올 수 없습니다.</Typography>
      </Box>
    );
  }

  const tableRows = convertTreeToRows(treeData);

  return (
    <Box sx={darkModeStyles.container}>
      <Paper
        elevation={6}
        sx={{
          ...darkModeStyles.paper,
          p: isNonMobile ? 4 : 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" sx={darkModeStyles.title}>
            트리뷰 - {params.id}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.history.go(-1)}
            sx={darkModeStyles.backButton}
          >
            목록으로 돌아가기
          </Button>
        </Box>

        <TableContainer sx={darkModeStyles.tableContainer}>
          <Table>
            <TableHead sx={darkModeStyles.tableHead}>
              <TableRow>
                <TableCell align="center" sx={darkModeStyles.tableCell}>NO</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>레벨1</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>레벨2</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>레벨3</TableCell>
                <TableCell align="center" sx={darkModeStyles.tableCell}>회원수</TableCell>
                <TableCell align="center" sx={darkModeStyles.tableCell}>에이전트 머니</TableCell>
                <TableCell align="center" sx={darkModeStyles.tableCell}>지갑</TableCell>
                <TableCell align="center" sx={darkModeStyles.tableCell}>요율</TableCell>
                <TableCell align="center" sx={darkModeStyles.tableCell}>상태</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderTableRows(tableRows)}</TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 지급 팝업 - 다크모드 스타일 적용 */}
      <Modal open={openModal} onClose={handleCancel}>
        <Box sx={darkModeStyles.modalBox}>
          <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
            {selectedAction} 처리
          </Typography>
          <TextField
            label={`${selectedAction} 금액`}
            value={paymentAmount}
            onChange={e => {
              const { value } = e.target;
              // 숫자만 허용
              if (/^[0-9]*$/.test(value)) {
                setPaymentAmount(value);
              }
            }}
            fullWidth
            sx={{ ...darkModeStyles.modalTextField, mb: 2 }}
            inputProps={{ maxLength: 30 }}
          />

          {/* 빠른 금액 버튼 추가 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button
              variant="contained"
              onClick={() => setPaymentAmount('50000000')}
              sx={{
                backgroundColor: 'rgba(96, 165, 250, 0.7)',
                color: 'white',
                borderRadius: '8px',
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                padding: '6px 12px',
                margin: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.9)',
                  boxShadow: '0 6px 8px rgba(0, 0, 0, 0.3)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              5천만
            </Button>
            <Button
              variant="contained"
              onClick={() => setPaymentAmount('100000000')}
              sx={{
                backgroundColor: 'rgba(96, 165, 250, 0.7)',
                color: 'white',
                borderRadius: '8px',
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                padding: '6px 12px',
                margin: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.9)',
                  boxShadow: '0 6px 8px rgba(0, 0, 0, 0.3)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              1억
            </Button>
            <Button
              variant="contained"
              onClick={() => setPaymentAmount('300000000')}
              sx={{
                backgroundColor: 'rgba(96, 165, 250, 0.7)',
                color: 'white',
                borderRadius: '8px',
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                padding: '6px 12px',
                margin: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.9)',
                  boxShadow: '0 6px 8px rgba(0, 0, 0, 0.3)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              3억
            </Button>
            <Button
              variant="contained"
              onClick={() => setPaymentAmount('500000000')}
              sx={{
                backgroundColor: 'rgba(96, 165, 250, 0.7)',
                color: 'white',
                borderRadius: '8px',
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                padding: '6px 12px',
                margin: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.9)',
                  boxShadow: '0 6px 8px rgba(0, 0, 0, 0.3)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              5억
            </Button>
          </Box>

          <Typography variant="body2" color="error" sx={{ mb: 2, px: 1 }}>
            에이전트 보유 금액보다 큰 금액을 지급할 수 없습니다. <br />
            대상 유저의 보유 금액보다 큰 금액을 차감할 수 없습니다.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.6)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              취소
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handlePaymentSubmit}
              disabled={paymentLoading}
              sx={{
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(33, 150, 243, 0.3)',
                  color: 'rgba(255, 255, 255, 0.4)'
                }
              }}
            >
              {selectedAction || '지급'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TreeViewTable;